import React, {useCallback, useMemo, useState} from 'react'
import './sign-points-tree.scss'

const GROUP_COMMERCIAL = '__commercial__'
const GROUP_TECHNICAL = '__technical__'

export const DOG_PROFILE_ID = '__dog_profile__'
export const DOG_PROFILE_TITLE = 'Суммарный профиль по договору'

/** Цикл: 0 → 1 (+) → -1 (−) → 0 */
function nextSign(sign) {
    if (sign === 1) return -1
    if (sign === -1) return 0
    return 1
}

function SignBox({sign}) {
    const cls =
        sign === 1 ? 'sign-box sign-box--plus' : sign === -1 ? 'sign-box sign-box--minus' : 'sign-box sign-box--none'
    const label = sign === 1 ? '+' : sign === -1 ? '−' : ''
    return (
        <span className={cls} aria-hidden="true">
            {label}
        </span>
    )
}

function DogProfileRow({checked, onToggle}) {
    return (
        <div className="sign-points-dog-row">
            <button
                type="button"
                className="sign-box-btn"
                title={checked ? 'Включено' : 'Не отмечено'}
                onClick={onToggle}
            >
                <span className={`sign-box sign-box--check${checked ? ' is-on' : ''}`} aria-hidden="true">
                    {checked ? '✓' : ''}
                </span>
            </button>
            <button type="button" className="sign-points-name-btn" onClick={onToggle}>
                {DOG_PROFILE_TITLE}
            </button>
        </div>
    )
}

function SignRow({id, title, sign, onCycle}) {
    return (
        <li className="sign-points-row">
            <button
                type="button"
                className="sign-box-btn"
                title={sign === 1 ? 'Плюс' : sign === -1 ? 'Минус' : 'Не отмечено'}
                onClick={() => onCycle(id)}
            >
                <SignBox sign={sign}/>
            </button>
            <button type="button" className="sign-points-name-btn" onClick={() => onCycle(id)}>
                {title}
            </button>
        </li>
    )
}

function GroupBlock({groupId, title, items, signs, expanded, onToggle, onCycle}) {
    return (
        <div className="sign-points-group-block">
            <button
                type="button"
                className="sign-points-group-header"
                onClick={() => onToggle(groupId)}
                aria-expanded={expanded}
            >
                <span className={`sign-points-chevron${expanded ? ' is-open' : ''}`}/>
                <span className="sign-points-group">{title}</span>
                <span className="sign-points-group-count">{items.length}</span>
            </button>
            {expanded && (
                <ul className="sign-points-list">
                    {items.length === 0 ? (
                        <li className="sign-points-empty">Нет точек</li>
                    ) : (
                        items.map((p) => (
                            <SignRow
                                key={p.id}
                                id={p.id}
                                title={p.t}
                                sign={signs[p.id] || 0}
                                onCycle={onCycle}
                            />
                        ))
                    )}
                </ul>
            )}
        </div>
    )
}

function buildSelected(signs, leaves) {
    const byId = new Map((leaves || []).map((p) => [p.id, p]))
    return Object.keys(signs)
        .filter((id) => signs[id])
        .map((id) => {
            const p = byId.get(id)
            if (!p) return null
            const isTech = !!p.isTech
            return {
                id: p.id,
                kodPoint: isTech ? null : (String(p.id).includes('_') ? String(p.id).split('_')[1] : String(p.id)),
                pointCode: p.pointCode || null,
                t: p.t,
                sign: signs[id],
                isTech,
                group: isTech || Number(p.askue) !== 1 ? GROUP_TECHNICAL : GROUP_COMMERCIAL,
            }
        })
        .filter(Boolean)
}

function techLeafId(p) {
    const stamp = String(p.dat_s ?? '').replace(/[^0-9]/g, '').slice(0, 14)
    return `tech_${p.kod_gtp}_${p.nomer}_${stamp}`
}

/**
 * Первая строка — двухпозиционный чекбокс «Суммарный профиль по договору».
 * Ниже два раскрываемых узла: коммерческий / технический.
 * Выбор точек: пусто / + / −. Состояние controlled через value/onChange.
 * technicalPoints — строки cc_gtp_point (kod_gtp, nomer, name, point_code, dat_s).
 */
export default function SignPointsTree({
    points = [],
    technicalPoints = [],
    value = [],
    onChange,
    dogProfile = false,
    onDogProfileChange,
}) {
    const [expanded, setExpanded] = useState({
        [GROUP_COMMERCIAL]: true,
        [GROUP_TECHNICAL]: true,
    })

    const signs = useMemo(() => {
        const m = {}
        for (const s of value || []) {
            if (s?.id != null && s.sign) m[s.id] = s.sign
        }
        return m
    }, [value])

    const {commercial, technical, leaves} = useMemo(() => {
        const commercialLeaves = (points || []).filter((p) => p && !p.isG && Number(p.askue) === 1)
        const technicalLeaves = (technicalPoints || []).map((p) => ({
            id: techLeafId(p),
            t: `ТУ №${p.nomer}: ${p.name || p.point_code || ''}`.trim(),
            isTech: true,
            pointCode: p.point_code,
            askue: 0,
        }))
        return {
            commercial: commercialLeaves,
            technical: technicalLeaves,
            leaves: [...commercialLeaves, ...technicalLeaves],
        }
    }, [points, technicalPoints])

    const cycle = useCallback((id) => {
        if (!onChange) return
        const next = {...signs, [id]: nextSign(signs[id] || 0)}
        if (!next[id]) delete next[id]
        onChange(buildSelected(next, leaves))
    }, [signs, leaves, onChange])

    const toggleGroup = useCallback((groupId) => {
        setExpanded((prev) => ({...prev, [groupId]: !prev[groupId]}))
    }, [])

    const toggleDogProfile = useCallback(() => {
        if (onDogProfileChange) onDogProfileChange(!dogProfile)
    }, [dogProfile, onDogProfileChange])

    return (
        <div className="sign-points-tree" id="sign-points-tree">
            <DogProfileRow checked={!!dogProfile} onToggle={toggleDogProfile}/>
            <GroupBlock
                groupId={GROUP_COMMERCIAL}
                title="Коммерческий учет (АСКУЭ)"
                items={commercial}
                signs={signs}
                expanded={!!expanded[GROUP_COMMERCIAL]}
                onToggle={toggleGroup}
                onCycle={cycle}
            />
            <GroupBlock
                groupId={GROUP_TECHNICAL}
                title="Технический учет (АСТУЭ)"
                items={technical}
                signs={signs}
                expanded={!!expanded[GROUP_TECHNICAL]}
                onToggle={toggleGroup}
                onCycle={cycle}
            />
        </div>
    )
}
